import { Plus, Heart, Eye, Trash2, MoreHorizontal, Check } from "lucide-react"
import { Link } from "@tanstack/react-router"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Attribution } from "@/components/AttributionPopover"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu"
import { unsplashWebp, unsplashSrcSet } from "@/lib/utils"

export type ExploreCardItem = {
  id: string
  // Canonical short id for building /guide/<slug>/<short_id> links. Optional
  // because some surfaces (the explore grid) build the href themselves from a
  // wider row; the destination hub relies on it to avoid linking by uuid.
  short_id?: string
  name: string | null
  destination: string
  description: string | null
  image_url: string | null
  image_attribution?: string | null
  image_attribution_url?: string | null
  like_count?: number
  view_count?: number
  owner_name?: string
  owner_image?: string
  isLiked?: boolean
}

interface ExploreCardProps {
  item: ExploreCardItem
  href: string
  onCopy: (id: string) => void
  onLike?: (id: string) => void
  onUnlike?: (id: string) => void
  onDelete?: (id: string) => void
  isCopying?: boolean
  showCopyButton?: boolean
  selectionMode?: boolean
  selected?: boolean
  onToggleSelect?: (id: string) => void
}

export function ExploreCard({
  item,
  href,
  onCopy,
  onLike,
  onUnlike,
  onDelete,
  isCopying = false,
  showCopyButton = true,
  selectionMode = false,
  selected = false,
  onToggleSelect,
}: ExploreCardProps) {
  const handleLikeClick = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (item.isLiked) {
      onUnlike?.(item.id)
    } else {
      onLike?.(item.id)
    }
  }

  return (
    <Link
      to={href as never}
      // Intent-preload: a grid of cards must not viewport-preload every guide
      // route chunk onto the home/explore waterfall — fetch on hover/touch.
      preload="intent"
      className="group cursor-pointer block"
      data-testid="explore-card"
      data-guide-id={item.id}
      onClick={
        selectionMode
          ? (e) => {
              e.preventDefault()
              onToggleSelect?.(item.id)
            }
          : undefined
      }
    >
      {/* Image Container */}
      <div className={`relative rounded-lg overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900 aspect-[3/4] mb-3 ${selected ? `ring-2 ring-primary ring-offset-2 ring-offset-background` : ``}`}>
        {/* Selection checkbox (selection mode only) */}
        {selectionMode && (
          <div className="absolute top-3 left-3 z-30">
            <div
              className={`w-6 h-6 rounded-full flex items-center justify-center border-2 transition-colors ${
                selected
                  ? `bg-primary border-primary text-primary-foreground`
                  : `bg-black/40 border-white/80 backdrop-blur-sm`
              }`}
            >
              {selected && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
            </div>
          </div>
        )}
        {item.image_url ? (
          <img
            src={unsplashWebp(item.image_url)}
            srcSet={unsplashSrcSet(item.image_url)}
            sizes="(max-width: 640px) 76vw, (max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
            alt={item.destination}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
          />
        ) : (
          // Coverless fallback: cover lookups now legitimately miss (Unsplash
          // misses, repaired older guides), so this is a designed empty state —
          // the slate gradient on the container plus a subtle destination label,
          // never a "No image" error chrome.
          <div className="absolute inset-0 flex items-end p-4">
            <span className="text-white/25 text-3xl font-extrabold tracking-tight leading-none line-clamp-2 select-none">
              {item.destination?.split(`,`)[0] || item.name}
            </span>
          </div>
        )}

        {/* Rich gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

        {/* Bottom overlay - description then attribution */}
        {(item.description || item.image_attribution) && (
          <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/85 to-transparent flex flex-col justify-end px-3 pb-3 pt-10 z-10">
            {item.description && (
              <p className="text-white text-xs leading-snug line-clamp-3 mb-1.5">{item.description}</p>
            )}
            {item.image_attribution && (
              <div className="flex justify-end">
                <Attribution
                  attribution={item.image_attribution}
                  attributionUrl={item.image_attribution_url}
                  className=""
                />
              </div>
            )}
          </div>
        )}

        {/* Top Section - stats and copy button */}
        <div className={`absolute top-3 inset-x-3 z-20 flex items-center justify-between ${selectionMode ? `hidden` : ``}`}>
          <div className="flex items-center gap-1.5">
            {item.like_count !== undefined && onLike ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    onClick={handleLikeClick}
                    aria-label={item.isLiked ? `Unlike, ${item.like_count} likes` : `Like, ${item.like_count} likes`}
                    className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1 transition-colors ${
                      item.isLiked
                        ? `bg-red-500/90 text-white`
                        : `bg-white/95 backdrop-blur-sm text-black hover:bg-red-100`
                    }`}
                  >
                    <Heart className={`w-3 h-3 ${item.isLiked ? `fill-current` : ``}`} />
                    {item.like_count}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{item.isLiked ? `Unlike` : `Like this guide`}</TooltipContent>
              </Tooltip>
            ) : item.like_count !== undefined ? (
              <div className="bg-white/95 backdrop-blur-sm text-black px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1">
                <Heart className="w-3 h-3" />
                {item.like_count}
              </div>
            ) : null}
            {item.view_count !== undefined && (
              <div className="bg-white/95 backdrop-blur-sm text-black px-2.5 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1">
                <Eye className="w-3 h-3" />
                {item.view_count}
              </div>
            )}
          </div>
          {showCopyButton && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    onCopy(item.id)
                  }}
                  disabled={isCopying}
                  className="w-9 h-9 rounded-full bg-white/20 hover:bg-white/30 text-white flex items-center justify-center transition-all duration-200 backdrop-blur-sm disabled:opacity-50"
                  aria-label="Add to my trips"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Add to my trips</TooltipContent>
            </Tooltip>
          )}
          {onDelete && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  onClick={(e) => e.preventDefault()}
                  data-testid="guide-card-menu-btn"
                  className="w-7 h-7 flex items-center justify-center rounded-full bg-black/50 backdrop-blur-sm hover:bg-black/70 transition-colors"
                  aria-label="Guide options"
                >
                  <MoreHorizontal className="w-3.5 h-3.5 text-white" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  data-testid="guide-card-delete-btn"
                  onClick={(e) => {
                    e.preventDefault()
                    onDelete(item.id)
                  }}
                  variant="destructive"
                >
                  <Trash2 className="w-3 h-3" /> Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Content Section */}
      <div className="space-y-2.5">
        <div className="space-y-1">
          <h3 className="text-sm font-bold line-clamp-2 group-hover:text-primary transition-colors">
            {item.name || item.destination}
          </h3>
          <p className="text-xs text-gray-500 line-clamp-1">{item.destination}</p>
        </div>

        {item.owner_name && (
          <div className="flex items-center gap-2">
            <Avatar className="w-6 h-6 border border-background">
              <AvatarImage src={item.owner_image || ``} alt={item.owner_name} />
              <AvatarFallback className="bg-gradient-to-br from-primary/80 to-primary text-white font-bold text-xs">
                {item.owner_name.charAt(0).toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs text-gray-600 font-medium truncate">
              by {item.owner_name}
            </span>
          </div>
        )}
      </div>
    </Link>
  )
}
